/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {AccountFilter} from '@ion/core/src/types/services';

type AddEscapeOptions = {
  /** Keys to process. If omitted, falls back to module-level `fieldsToFix`. */
  fieldsToFix?: string[];
  /** Escape character; defaults to `defaultEscapeChar` when not provided. */
  escapeChar?: string;
  /** Character to escape; defaults to `defaultCharToScape` when not provided. */
  charToEscape?: string;
};

const defaultEscapeChar = '\\';

const defaultCharToScape = ',';

const defaultFieldsToFix = ['name', 'parent.name'];

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 *  addEscapeCharsBeforeChar - returns a string with escapes.
 * 
 * ```typescript
 * 
 * import {addEscapeCharsToFilter} from '@ion/dls/src/helpers/setEscapeCharsToFilter';;
 *
 * addEscapeCharsBeforeChar('a,b,c', '\\', ','); // returns 'a\\,b\\,c'
 * addEscapeCharsBeforeChar('a,b', '**', ',');   // returns 'a**,b'
 * addEscapeCharsBeforeChar('1.2.3', '\\', '.'); // returns '1\\.2\\.3'
 * 
 * addEscapeCharsBeforeChar('', '\\', ',');      // returns ''
 * addEscapeCharsBeforeChar('abc', '\\', '');    // returns 'abc'
 * @param str        The input string to process.
 * @param escapeChar The escape sequence to prepend before every occurrence of `char`.
 * @param char       The literal character to escape (e.g., ',' or '.').
 * @returns A new string where each literal occurrence of `char` is prefixed by `escapeChar`.
 */
export const addEscapeCharsBeforeChar = (
  str: string,
  escapeChar: string,
  char: string
): string => {
  // Behavior: if there is no input string or no target character, no transformation is performed.
  if (!str || !char) {
    return str;
  }

  // Build a global regex that matches the literal character (special regex symbols are escaped).
  const re = new RegExp(escapeRegex(char), 'g');

  // `$&` reinjects the exact literal match (the target character) after the chosen escape sequence.
  return str.replace(re, `${escapeChar}$&`);
};

/**
 *  addEscapeCharsToFilter - returns a new object with selected fields escaped (immutable).
 * 
 * ```typescript
 * import { addEscapeCharsToFilter } from '@ion/dls/src/helpers/setEscapeCharsToFilter';
 *
 * addEscapeCharsToFilter({ key_1: 'a,b', key_2: 'x,y', key_3: 'ignore' }); 
 * // returns { key_1: 'a\\,b', key_2: 'x\\,y', key_3: 'ignore' }
 *
 * addEscapeCharsToFilter(
 *   { foo: '1,2', bar: '3,4', baz: '5,6' },
 *   { fieldsToFix: ['foo', 'baz'] }
 * );
 * // returns { foo: '1\\,2', bar: '3,4', baz: '5\\,6' }
 *
 * addEscapeCharsToFilter({ key_1: 'a,b', key_3: 'not,touched' });
 * // returns { key_1: 'a\\,b', key_3: 'not,touched' }
 * ```
 *
 * @param filter   The input object whose string fields may be escaped.
 * @param options  Optional settings to control behavior:
 *                 - fieldsToFix?: array of keys to process; if omitted, module-level defaults are used.
 *                 - escapeChar?: the escape sequence to prepend before each occurrence of `charToEscape`.
 *                 - charToEscape?: the literal character to escape (e.g., ',' or '.').
 * @returns A new object preserving all original keys, where only the selected fields have their target
 *          character occurrences prefixed by the escape sequence.
 */
export const addEscapeCharsToFilter = (filter: AccountFilter, options?: AddEscapeOptions): AccountFilter => {
  const escapeChar = options?.escapeChar ? options.escapeChar : defaultEscapeChar;

  const charToEscape = options?.charToEscape ? options.charToEscape : defaultCharToScape;

  // If `fieldsToFix` is not provided on options, fallback to the module-level one.
  const fieldsToFix = options?.fieldsToFix ? options.fieldsToFix : defaultFieldsToFix;

  const fixedFilter = fieldsToFix.reduce((acc, key) => {
    if (filter[key]) {
      const val = filter[key];
      const newVal = addEscapeCharsBeforeChar(val, escapeChar, charToEscape);

      acc[key] = newVal;
    }
    return acc;
  }, {...filter});

  return fixedFilter;
};
